import React, { createContext, useContext, useState, useEffect } from 'react';
import { useUserRole } from '@/hooks/useUserRole';

type ViewOverride = 'mobile' | 'tablet' | null;

interface AdminViewContextType {
  viewOverride: ViewOverride;
  setViewOverride: (view: ViewOverride) => void;
  isOverrideActive: boolean;
  simulatedWidth: number | null;
}

const AdminViewContext = createContext<AdminViewContextType | undefined>(undefined);

const STORAGE_KEY = 'admin_view_override';

export function AdminViewProvider({ children }: { children: React.ReactNode }) {
  const { isAdmin, loading: roleLoading } = useUserRole();
  const [viewOverride, setViewOverrideState] = useState<ViewOverride>(() => {
    if (typeof window === 'undefined') return null;
    const stored = localStorage.getItem(STORAGE_KEY);
    return (stored === 'mobile' || stored === 'tablet') ? stored : null;
  });

  // Clear override if user is not admin
  useEffect(() => {
    if (!roleLoading && !isAdmin && viewOverride) {
      setViewOverrideState(null);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [isAdmin, roleLoading, viewOverride]);

  const setViewOverride = (view: ViewOverride) => {
    if (!isAdmin) return; // Security check
    
    setViewOverrideState(view);
    if (view) {
      localStorage.setItem(STORAGE_KEY, view);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const simulatedWidth = viewOverride === 'mobile' ? 375 : viewOverride === 'tablet' ? 768 : null;

  return (
    <AdminViewContext.Provider
      value={{
        viewOverride,
        setViewOverride,
        isOverrideActive: isAdmin && viewOverride !== null,
        simulatedWidth,
      }}
    >
      {children}
    </AdminViewContext.Provider>
  );
}

export function useAdminView() {
  const context = useContext(AdminViewContext);
  if (context === undefined) {
    throw new Error('useAdminView must be used within AdminViewProvider');
  }
  return context;
}
